import React, { useState, useEffect, useCallback } from 'react';
import { campaignsAPI, invitationsAPI } from '../../../../services/api';
import CharacterSheetModal from '../../CharacterSheetModal';
import './Players.css';

function Players({ campaign }) {
  const [players, setPlayers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [showCharacterModal, setShowCharacterModal] = useState(false);

  const [showXpModal, setShowXpModal] = useState(false);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const [xpAmount, setXpAmount] = useState('');
  const [xpReason, setXpReason] = useState('');
  const [xpTarget, setXpTarget] = useState(null); // null = all players, or specific player ID
  const [isSavingXp, setIsSavingXp] = useState(false);

  const [revokingId, setRevokingId] = useState(null);

  const normalizeCharacterSheet = (raw) => {
    const c = raw || {};
    const s = c.stats || {};

    return {
      ...c,
      // flatten stats so existing UI works
      physique: c.physique ?? s.physique,
      coordination: c.coordination ?? s.coordination,
      logic: c.logic ?? s.logic,
      perception: c.perception ?? s.perception,
      presence: c.presence ?? s.presence,
      resilience: c.resilience ?? s.resilience,

      // normalize type label (sheet uses characterType OR aetherionType)
      characterType: c.characterType ?? c.aetherionType,
    };
  };

  // ---------- Aetherion tolerance helpers ----------
  const normAetherionType = (t) => (t || '').toString().trim().toLowerCase();

  const labelAetherionType = (t) => {
    const v = normAetherionType(t);
    if (v === 'ascended') return 'Ascended';
    if (v === 'tolerant') return 'Tolerant';
    if (v === 'normal') return 'Normal';
    return t || 'Unknown';
  };

  const badgeAetherionType = (t) => {
    const v = normAetherionType(t);
    if (v === 'ascended') return '⚡ Ascended';
    if (v === 'tolerant') return '◐ Tolerant';
    if (v === 'normal') return '○ Normal';
    return '—';
  };
  // -----------------------------------------------

  const loadPlayers = useCallback(async () => {
    if (!campaign?.id) {
      setPlayers([]);
      return;
    }

    const res = await campaignsAPI.getCampaignPlayers(campaign.id);
    setPlayers(res.data?.players || []);
  }, [campaign?.id]);

  const loadPendingInvites = useCallback(async () => {
    if (!campaign?.id) {
      setPendingInvites([]);
      return;
    }

    const res = await invitationsAPI.getCampaignInvitations(campaign.id);
    const invites = res.data?.invitations || [];
    setPendingInvites(invites.filter(i => (i.status || '').toLowerCase() === 'pending'));
  }, [campaign?.id]);

  const loadAll = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await Promise.all([loadPlayers(), loadPendingInvites()]);
    } catch (e) {
      console.error('Failed to load players/invitations:', e);
      setError(e?.response?.data?.error || 'Failed to load players/invitations');
    } finally {
      setIsLoading(false);
    }
  }, [loadPlayers, loadPendingInvites]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleViewCharacter = async (player) => {
    try {
      const res = await campaignsAPI.getCharacterSheet(player.characterId);
      setSelectedCharacter(normalizeCharacterSheet(res.data?.character));
      setShowCharacterModal(true);
    } catch (e) {
      console.error('Failed to load character:', e);
      alert('Failed to load character sheet');
    }
  };

  const handleAwardXp = (playerId = null) => {
    setXpTarget(playerId);
    setXpAmount('');
    setXpReason('');
    setShowXpModal(true);
  };

  const handleSubmitXp = async () => {
    const amount = parseInt(xpAmount, 10);
    if (!amount || amount <= 0) {
      alert('Please enter a valid XP amount');
      return;
    }

    try {
      setIsSavingXp(true);

      if (xpTarget) {
        await campaignsAPI.awardXp(campaign.id, xpTarget, amount, xpReason);
      } else {
        await campaignsAPI.awardXpToAll(campaign.id, amount, xpReason);
      }

      await loadPlayers();
      setShowXpModal(false);
      setXpAmount('');
      setXpReason('');
    } catch (e) {
      console.error('Failed to award XP:', e);
      alert(e?.response?.data?.error || 'Failed to award XP');
    } finally {
      setIsSavingXp(false);
    }
  };

  const closeCharacterModal = () => {
    setShowCharacterModal(false);
    setSelectedCharacter(null);
  };

  const closeXpModal = () => {
    setShowXpModal(false);
    setXpTarget(null);
    setXpAmount('');
    setXpReason('');
  };

  const openInviteModal = () => {
    setInviteEmail('');
    setInviteMessage('');
    setShowInviteModal(true);
  };

  const closeInviteModal = () => {
    if (isInviting) return;
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteMessage('');
  };

  const handleSendInvite = async () => {
    const email = (inviteEmail || '').trim().toLowerCase();
    const message = (inviteMessage || '').trim();
    if (!email) {
      alert('Please enter an email address');
      return;
    }
    if (!campaign?.id) return;

    try {
      setIsInviting(true);
      await invitationsAPI.invitePlayer(campaign.id, { email, message });
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteMessage('');
      await loadPendingInvites();
      alert('Invitation sent');
    } catch (e) {
      console.error('Failed to send invitation:', e);
      alert(e?.response?.data?.error || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRevokeInvite = async (inv) => {
    const label = inv.invitedUserName || inv.invitedUserEmail || 'this user';
    const ok = window.confirm(`Revoke invitation for ${label}?`);
    if (!ok) return;

    try {
      setRevokingId(inv.id);
      setError(null);

      if (!invitationsAPI.revokeInvitation) {
        alert('revokeInvitation API method not found. Add it to invitationsAPI in services/api.js.');
        return;
      }

      await invitationsAPI.revokeInvitation(inv.id);

      // optimistic update
      setPendingInvites(prev => prev.filter(i => i.id !== inv.id));
    } catch (e) {
      console.error('Failed to revoke invitation:', e);
      setError(e?.response?.data?.error || 'Failed to revoke invitation');
    } finally {
      setRevokingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="players-section">
        <div className="loading-state">Loading players...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="players-section">
        <div className="error-message">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="players-section">
      <div className="section-header">
        <h2>Campaign Players</h2>
        <div className="section-actions">
          <button className="btn-secondary" onClick={openInviteModal}>
            Invite Player
          </button>
          <button className="btn-primary" onClick={() => handleAwardXp(null)}>
            Award XP to All
          </button>
        </div>
      </div>

      {/* ✅ Pending Invitations (GM) */}
      <div style={{ marginBottom: 26 }}>
        <h3 style={{ margin: '0 0 12px 0' }}>Pending Invitations</h3>

        {pendingInvites.length === 0 ? (
          <div className="empty-state">
            <p>No pending invitations.</p>
          </div>
        ) : (
          <div className="players-grid">
            {pendingInvites.map(inv => (
              <div
                key={inv.id}
                className="player-card"
                style={{ borderStyle: 'dashed' }}
              >
                <div className="player-header">
                  <div className="player-info">
                    <h3 style={{ marginBottom: 6 }}>
                      {inv.invitedUserName || inv.invitedUserEmail || 'Invited Player'}
                    </h3>
                    <p className="player-name" style={{ margin: 0 }}>
                      {inv.invitedUserEmail || ''}
                    </p>
                  </div>
                  <div className="player-type-badge">⏳ Pending</div>
                </div>

                {inv.message ? (
                  <div style={{
                    marginBottom: 14,
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8,
                    padding: 12
                  }}>
                    <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: 6 }}>Message</div>
                    <div style={{ fontSize: '0.95rem' }}>{inv.message}</div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 14, opacity: 0.8, fontStyle: 'italic' }}>
                    No message attached.
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>
                    Sent: {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}
                  </div>

                  <button
                    className="btn-secondary"
                    onClick={() => handleRevokeInvite(inv)}
                    disabled={revokingId === inv.id}
                    title="Revoke this invitation"
                  >
                    {revokingId === inv.id ? 'Revoking...' : 'Revoke'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ Active Players */}
      <div style={{ marginTop: 26 }}>
        <h3 style={{ margin: '0 0 12px 0' }}>Active Players</h3>

        {players.length === 0 ? (
          <div className="empty-state">
            <p>No players have joined this campaign yet.</p>
            <p className="hint">Players can join by accepting a campaign invitation.</p>
          </div>
        ) : (
          <div className="players-grid">
            {players.map(player => (
              <div key={player.id} className="player-card">
                <div className="player-header">
                  <div className="player-info">
                    <h3>{player.characterName}</h3>
                    <p className="player-name">Played by {player.playerName}</p>

                    <p className="player-name" style={{ marginTop: 4, opacity: 0.9 }}>
                      Aetherion Tolerance: <strong>{labelAetherionType(player.aetherionType)}</strong>
                    </p>
                  </div>

                  <div className="player-type-badge">
                    {badgeAetherionType(player.aetherionType)}
                  </div>
                </div>

                <div className="player-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total XP</span>
                    <span className="stat-value">{player.totalXp || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Available XP</span>
                    <span className="stat-value">{player.availableXp || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Level</span>
                    <span className="stat-value">{player.level || 1}</span>
                  </div>
                </div>

                <div className="player-quick-stats">
                  <div className="quick-stat">
                    <span className="quick-stat-label">HP:</span>
                    <span className="quick-stat-value">{player.currentHp}/{player.maxHp}</span>
                  </div>
                  <div className="quick-stat">
                    <span className="quick-stat-label">Charges:</span>
                    <span className="quick-stat-value">
                      {normAetherionType(player.aetherionType) === 'normal'
                        ? 'N/A'
                        : `${player.currentCharges || 0}/5`}
                    </span>
                  </div>
                </div>

                <div className="player-actions">
                  <button className="btn-secondary" onClick={() => handleViewCharacter(player)}>
                    View Character
                  </button>
                  <button className="btn-secondary" onClick={() => handleAwardXp(player.id)}>
                    Award XP
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Player Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={closeInviteModal}>
          <div className="modal-content invite-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Invite Player</h2>
              <button className="modal-close" onClick={closeInviteModal}>×</button>
            </div>

            <div className="modal-body">
              <p className="invite-hint">
                Enter the player&apos;s account email. They&apos;ll see the invitation on their dashboard.
              </p>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="player@example.com"
                  disabled={isInviting}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Message (Optional)</label>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="E.g., 'We play Fridays at 8pm ET. Bring a scavenger crew.'"
                  rows={3}
                  disabled={isInviting}
                />
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" onClick={closeInviteModal} disabled={isInviting}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleSendInvite} disabled={isInviting}>
                  {isInviting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Shared full Character Sheet Modal (now includes derived combat stats) */}
      <CharacterSheetModal
        isOpen={showCharacterModal}
        character={selectedCharacter}
        onClose={closeCharacterModal}
      />

      {/* XP Award Modal */}
      {showXpModal && (
        <div className="modal-overlay" onClick={closeXpModal}>
          <div className="modal-content xp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {xpTarget
                  ? `Award XP to ${players.find(p => p.id === xpTarget)?.characterName}`
                  : 'Award XP to All Players'}
              </h2>
              <button className="modal-close" onClick={closeXpModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>XP Amount</label>
                <input
                  type="number"
                  value={xpAmount}
                  onChange={(e) => setXpAmount(e.target.value)}
                  placeholder="Enter XP amount"
                  min="1"
                  disabled={isSavingXp}
                />
              </div>

              <div className="form-group">
                <label>Reason (Optional)</label>
                <textarea
                  value={xpReason}
                  onChange={(e) => setXpReason(e.target.value)}
                  placeholder="E.g., 'Defeated the dragon', 'Completed Chapter 1'"
                  rows={3}
                  disabled={isSavingXp}
                />
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" onClick={closeXpModal} disabled={isSavingXp}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleSubmitXp} disabled={isSavingXp}>
                  {isSavingXp ? 'Awarding...' : 'Award XP'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Players;
